<template>
  <v-container fluid>
    <v-layout>
      <v-flex class="headline">{{ $t('generalSettings') }}</v-flex>
    </v-layout>
    <v-divider></v-divider>
    <v-form ref="general" v-model="isGeneralValid" lazy-validation>
      <v-layout>
        <v-flex md4 lg3>
          <v-switch
            :label="$t(generalSettings.isMeasurementMetric ? `metric(kg/cm)` : 'imperial(lbs/inch)')"
            v-model="generalSettings.isMeasurementMetric"
          ></v-switch>
        </v-flex>
        <v-flex md4 lg3>
          <v-switch
            :label="$t('scanForm')"
            v-model="generalSettings.isScanForm"
          ></v-switch>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-icon color="primary">info</v-icon>
        {{ $t('metricSystemIsOnlyForInputData') }}
      </v-layout>
      <v-layout>
        <v-flex md4 lg3>
          <v-switch
            :label="$t(generalSettings.enableAmazonSites ? `amazonSites` : 'disabledAmazonSites')"
            v-model="generalSettings.enableAmazonSites"
          ></v-switch>
        </v-flex>
      </v-layout>
    </v-form>
    <v-layout>
      <v-flex class="headline">{{ $t('featureSettings') }}</v-flex>
    </v-layout>
    <v-divider></v-divider>
    <v-form ref="feature" v-model="isFeatureValid" lazy-validation>
      <v-layout row wrap>
        <v-flex md4>
          <v-checkbox :label="$t('skuMode')" v-model="generalSettings.skuMode" />
        </v-flex>
        <v-flex md4>
          <v-checkbox :label="$t('advancedOutboundMode')" v-model="generalSettings.advancedOutbound" />
        </v-flex>
        <v-flex md4>
          <v-checkbox :label="$t('prescan')" v-model="generalSettings.activatePrescan" />
        </v-flex>
      </v-layout>
      <v-layout row wrap>
        <v-flex md4>
          <v-checkbox :label="$t('enableInboundSNScan')" v-model="generalSettings.enableSnScan"></v-checkbox>
        </v-flex>
        <v-flex md4>
          <v-checkbox :label="$t('measureDimension')" v-model="generalSettings.measureDimension"></v-checkbox>
        </v-flex>
        <v-flex md4>
          <v-checkbox :label="$t('scanIMEI')" v-model="generalSettings.enableIMEIScan"></v-checkbox>
        </v-flex>
        <v-flex md4 mb-2>
          <v-layout>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <a 
                  href="https://qz.io/download/" 
                  target="_blank" 
                  class="blue--text text-decoration-underline font-weight-medium mr-2"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ $t('autoPrintTool') }}
                  <v-icon small color="blue">open_in_new</v-icon>
                </a>
              </template>
              <span>{{ $t('autoPrintHelp') }}</span>
            </v-tooltip>
            
            <v-tooltip top class="ml-4">
              <template v-slot:activator="{ on, attrs }">
                <a 
                  href="../../static/qz-certificate.txt"
                  download
                  class="blue--text text-decoration-underline font-weight-medium"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ $t('downloadCert') }}
                  <v-icon small color="blue">get_app</v-icon>
                </a>
              </template>
              <span>{{ $t('downloadCertHelp') }}</span>
            </v-tooltip>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-form>
    <v-layout>
      <v-flex class="headline">{{ $t('forecastSettings') }}</v-flex>
    </v-layout>
    <v-divider></v-divider>
    <v-form ref="forecast" v-model="isForecastValid" lazy-validation>
      <v-layout>
        <v-flex md3>
          <span style="color: rgba(0, 0, 0, 0.54)">{{ $t('standardSmallBox') }}</span>
          <v-layout align-center>
            <v-flex xs4 class="mr-3">
              <v-text-field
                class="table-item"
                hide-details
                v-model.number="generalSettings['standardSmallBox'].length"
                :suffix="sizeSuffix"
                :rules="[fieldIsRequired('length'), fieldIsOverZero('length')]"
              ></v-text-field>
            </v-flex>
            <v-flex xs4 class="mr-3">
              <v-text-field
                class="table-item"
                hide-details
                v-model.number="generalSettings['standardSmallBox'].width"
                :suffix="sizeSuffix"
                :rules="[fieldIsRequired('width'), fieldIsOverZero('width')]"
              ></v-text-field>
            </v-flex>
            <v-flex xs4>
              <v-text-field
                class="table-item"
                hide-details
                v-model.number="generalSettings['standardSmallBox'].height"
                :suffix="sizeSuffix"
                :rules="[fieldIsRequired('height'), fieldIsOverZero('height')]"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-form>
    <v-layout>
      <v-flex class="headline">{{ $t('defaultSettings') }}</v-flex>
    </v-layout>
    <v-divider></v-divider>
    <v-form ref="default" v-model="isDefaultValid" lazy-validation>
      <v-layout>
        <v-flex md4 lg3>
          <v-autocomplete
            :items="organizations"
            item-text="organizationId"
            item-value="key"
            v-model="generalSettings.defaultOrgId"
            :label="$t('defaultOrganizationId')"
            clearable
          ></v-autocomplete>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex md4 lg3>
          <v-autocomplete
            :items="warehousesSites"
            item-text="siteName"
            item-value="key"
            v-model="defaultSite"
            :label="$t('defaultWarehouseSite')"
            clearable
          ></v-autocomplete>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex md4 lg3>
          <v-combobox
            :items="deliveryChannelList"
            v-model="selectedChannels"
            item-text="displayName"
            item-value="key"
            :label="$t('verifyLabelTypes')"
            :return-object="false"
            required
            chips
            multiple
            clearable 
            ></v-combobox>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex>
          <v-btn color="primary" @click="() => { ignoredCheckUpcsInEdit = generalSettings.ignoredCheckUpcs || []; showPopup = true }">{{ $t('ignoredCheckUpcs') }}</v-btn>
          <FormSubmitPopup
            v-model="showPopup"
            :title="$t('ignoredCheckUpcs')"
            medium
            @popupClose="() => { showPopup = false; ignoredCheckUpcsInEdit = [] }"
            :rightMethod="confirmSelection"
          >
            <template v-slot:input>
              <v-container>
                <v-toolbar>
                  <v-text-field
                    v-model.trim="search"
                    append-icon="filter_list"
                    :label="$t('filter')"
                    single-line
                    clearable
                    hide-details
                  ></v-text-field>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn 
                        color="primary" 
                        @click="selectAllItems"
                        v-on="on"
                      >{{ $t('selectAll') }}</v-btn>
                    </template>
                    <span>{{ $t('selectAllFilteredItems') }}</span>
                  </v-tooltip>
                  <v-btn color="primary" @click="deselectAllItems">{{ $t('deselectAll') }}</v-btn>
                </v-toolbar>

                <v-data-iterator
                  :items="filteredUpcs"
                  :rows-per-page-items="[100, 200, 500]"
                  :items-per-page.sync="itemsPerPage"
                  :page.sync="page"
                  class="my-4"
                  content-tag="v-layout"
                  row
                  wrap
                >
                  <template v-slot:item="{item}">
                    <v-flex xs6>
                      <v-checkbox
                        :input-value="ignoredCheckUpcsInEdit.includes(item)"
                        @change="toggleIgnoredCheckUpcs(item)"
                        class="mr-2"
                        :label="item"
                      ></v-checkbox>
                    </v-flex>
                  </template>
                </v-data-iterator>
                <v-footer>
                  <v-layout align-center>
                    <div class="status-bar">
                      {{ $t('selected') }} <span>{{ selectedCount }}</span> UPC
                    </div>
                    <v-spacer></v-spacer>
                    <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn 
                        color="primary" 
                        @click="selectAllItems"
                        v-on="on"
                      >{{ $t('selectAll') }}</v-btn>
                    </template>
                      <span>{{ $t('selectAllFilteredItems') }}</span>
                    </v-tooltip>
                    <v-btn color="primary" @click="deselectAllItems">{{ $t('deselectAll') }}</v-btn>
                  </v-layout>
                </v-footer>
              </v-container>
            </template>
          </FormSubmitPopup>
        </v-flex>
      </v-layout>
    </v-form>
    <v-layout>
      <v-flex md3 lg2>
        <v-btn color="primary" @click="onSubmitted">{{ $t('save') }}</v-btn>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { checkRules } from '@/utils/tools'
import FormSubmitPopup from './FormSubmitPopup.vue'
export default {
  name: 'GeneralSettings',
  components: {
    FormSubmitPopup
  },
  data () {
    return {
      generalSettings: {
        skuMode: false,
        advancedOutbound: false,
        activatePrescan: false,
        defaultOrgId: '',
        enableAmazonSites: false,
        enableSnScan: false,
        isMeasurementMetric: false,
        measureDimension: false,
        standardSmallBox: {
          length: 0,
          width: 0,
          height: 0
        },
        ignoredCheckUpcs: [],
        enableIMEIScan: false,
        isScanForm: false
      },
      defaultSite: window.localStorage.getItem('defaultSite' + this.$store.getters.uid),
      isGeneralValid: true,
      isFeatureValid: true,
      isForecastValid: true,
      isDefaultValid: true,
      menu: [],
      globalFeatures: {
        measureDimension: false
      },
      deliveryChannelList: [
        {
          displayName: 'Fedex',
          key: 'Fedex'
        }, 
        {
          displayName: 'UPS',
          key: 'UPS'
        },
        {
          displayName: 'USPS',
          key: 'USPS'
        }
      ],
      selectedChannels: [],
      showPopup: false,
      selectAll: false,
      search: '',
      itemsPerPage: 100,
      page: 1,
      ignoredCheckUpcsInEdit: []
    }
  },
  mixins: [checkRules],
  mounted () {
    Object.keys(this.generalSettings).forEach(key => {
      if (this.warehouseInfo.generalSettings) {
        this.generalSettings[key] = this.warehouseInfo.generalSettings[key] === undefined ? this.generalSettings[key] : this.warehouseInfo.generalSettings[key]
        if (this.globalFeatures[key] !== undefined) this.globalFeatures[key] = this.generalSettings[key]
      }
    })
    this.selectedChannels = this.warehouseInfo.defaultLabelCheckChannels
  },
  computed: {
    selectedCount () {
      return this.ignoredCheckUpcsInEdit.length
    },
    upcMap () {
      return this.$store.getters.upcMap || {}
    },
    upcs () {
      return Object.keys(this.upcMap)
    },
    sizeSuffix () {
      return this.$t('inch')
    },
    warehouseInfo () {
      return this.$store.getters.warehouseInfo
    },
    organizations () {
      return this.$store.getters.warehouseOrganizations.filter(element => {
        return element.organizationId !== ''
      })
    },
    warehousesSites () {
      return this.$store.getters.warehousesSites
    },
    filteredUpcs () {
      if (!this.search) return this.upcs
      return this.upcs.filter(upc => 
        upc.toLowerCase().includes(this.search.toLowerCase())
      )
    }
  },
  methods: {
    toggleIgnoredCheckUpcs (upc) {
      const index = this.ignoredCheckUpcsInEdit.indexOf(upc)
      if (index > -1) {
        this.ignoredCheckUpcsInEdit.splice(index, 1)
      } else {
        this.ignoredCheckUpcsInEdit.push(upc)
      }
    },
    toggleSelectAll () {
      if (this.selectAll) {
        this.ignoredCheckUpcsInEdit = this.filteredUpcs.map(item => item)
      } else {
        this.ignoredCheckUpcsInEdit = []
      }
    },
    selectAllItems () {
      this.selectAll = true
      this.toggleSelectAll()
    },
    deselectAllItems () {
      this.selectAll = false
      this.toggleSelectAll()
    },
    confirmSelection () {
      this.generalSettings.ignoredCheckUpcs = this.ignoredCheckUpcsInEdit
      this.showPopup = false
    },
    changeIgnoredCheckUpcs (upcs) {
      this.ignoredCheckUpcsInEdit = upcs.filter(upc => this.upcMap[upc])
    },
    onSubmitted () {
      if (!this.$refs.general.validate() || !this.$refs.feature.validate() || !this.$refs.forecast.validate() || !this.$refs.default.validate()) {
        this.$store.dispatch('showToast', {info: `Form validate failed.`})
        return Promise.reject(Error('Form validate failed.'))
      }
      if (this.defaultSite) {
        window.localStorage.setItem('defaultSite' + this.$store.getters.uid, this.defaultSite)
      } else {
        window.localStorage.removeItem('defaultSite' + this.$store.getters.uid)
      }
      return this.submitSettings()
    },
    submitSettings () {
      let payload = {
        generalSettings: this.generalSettings,
        defaultLabelCheckChannels: this.selectedChannels
      }
      Object.keys(this.globalFeatures).forEach(feature => {
        if (this.generalSettings[feature] !== undefined && this.generalSettings[feature] !== this.globalFeatures[feature]) {
          payload.isNeedUpdateWarehouseLimitInfo = true
          !payload['featuresInOrg'] && (payload['featuresInOrg'] = [])
          this.generalSettings[feature] && payload['featuresInOrg'].push(feature)
        }
      })
      this.dispatchAndToast(this.$store.dispatch('changeWarehouseLimitedInfo', payload), 'Settings updated')
    },
    dispatchAndToast (promise, actionText) {
      return promise
        .then(rtn => {
          this.$store.dispatch('showToast', {info: `${actionText} ${this.$t('successfully')}`, level: 'success'})
          Object.keys(this.globalFeatures).forEach(feature => {
            if (this.generalSettings[feature] !== undefined) {
              this.globalFeatures[feature] = this.generalSettings[feature]
            }
          })
        })
        .catch(error => {
          this.$store.dispatch('showToast', {info: `${actionText} ${this.$t('failed')}` + error.message})
        })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/WarehouseGeneralSettings.vue